/**
 * Unsplash image search adapter. Normalized interface for the unified image picker.
 * Triggers download tracking per Unsplash API guidelines.
 */

import { getStoredValue, setStoredValue } from './api-key-storage.js';

const API_URL = 'https://api.unsplash.com/search/photos';
const PER_PAGE = 30;
const SEARCH_TIMEOUT_MS = 10_000;
const DOWNLOAD_TIMEOUT_MS = 15_000;
const LS_KEY = 'pb-unsplash-api-key';

const MIME_TO_EXT = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
  'image/gif': '.gif',
  'image/avif': '.avif'
};

export const unsplash = {
  name: 'Unsplash',
  needsKey: true,
  lsKey: LS_KEY,
  attribution: 'Photos provided by Unsplash',
  keyHelp: 'Get a free key at unsplash.com/developers',

  getApiKey() {
    return getStoredValue(LS_KEY);
  },

  setApiKey(key) {
    setStoredValue(LS_KEY, key);
  },

  async search(query, page, signal) {
    const key = this.getApiKey();
    if (!key) throw Object.assign(new Error('No API key'), { code: 'NO_KEY' });

    const url = `${API_URL}?query=${encodeURIComponent(query)}&per_page=${PER_PAGE}&page=${page}`;
    const combined = signal
      ? AbortSignal.any([signal, AbortSignal.timeout(SEARCH_TIMEOUT_MS)])
      : AbortSignal.timeout(SEARCH_TIMEOUT_MS);

    const resp = await fetch(url, {
      headers: { Authorization: `Client-ID ${key}` },
      signal: combined
    });

    if (resp.status === 401) throw Object.assign(new Error('Invalid API key'), { code: 'AUTH' });
    if (resp.status === 403) throw Object.assign(new Error('Rate limited'), { code: 'RATE' });
    if (!resp.ok) throw new Error(`Unsplash API error: ${resp.status}`);

    const data = await resp.json();

    return {
      results: data.results.map((p) => ({
        id: p.id,
        thumb: p.urls.small,
        title: p.description || p.alt_description || 'Stock photo',
        author: p.user.name,
        authorUrl: p.user.links.html,
        _raw: p
      })),
      total: data.total
    };
  },

  async download(item) {
    const photo = item._raw;

    // Trigger Unsplash download tracking (required by API guidelines)
    if (photo.links?.download_location) {
      const key = this.getApiKey();
      if (key) {
        fetch(photo.links.download_location, {
          headers: { Authorization: `Client-ID ${key}` }
        }).catch(() => {
          /* best-effort tracking */
        });
      }
    }

    const resp = await fetch(photo.urls.regular, {
      signal: AbortSignal.timeout(DOWNLOAD_TIMEOUT_MS)
    });
    if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);

    const blob = await resp.blob();
    const mime = blob.type || resp.headers.get('Content-Type') || 'image/jpeg';
    const ext = MIME_TO_EXT[mime] || '.jpg';
    return new File([blob], `unsplash-${photo.id}${ext}`, { type: mime });
  }
};
